import type { track as TrackFn } from "pro-features/components/WithTrack/src/context";

import type { TeamFilters } from "../../types/teams.types";

export type AssignUserClickSource = "assigned_bulk" | "unassigned_bulk" | "unassigned_row";

export type TeamsManagementTabTrackingName = "teams" | "assigned users" | "unassigned users";

const getActiveFilterKeys = (filters: TeamFilters): string[] =>
    Object.entries(filters)
        .filter(([, value]) => {
            if (value == null) {
                return false;
            }
            if (Array.isArray(value)) {
                return value.length > 0;
            }
            if (typeof value === "object") {
                return Object.keys(value).length > 0;
            }
            return true;
        })
        .map(([key]) => key);

export const createTeamsTrackingService = (track: TrackFn) => {
    const trackTeamsManagementEvent = (action: string, name: string) => {
        track("sales teams management", action, name);
    };

    return {
        trackTeamCreated(teamName: string, memberCount: number) {
            trackTeamsManagementEvent("create team", `${teamName}/${memberCount}`);
        },
        trackMembersAdded(teamName: string, addedCount: number) {
            trackTeamsManagementEvent("add members", `${teamName}/${addedCount}`);
        },
        trackFiltersSaved(teamName: string, filters: TeamFilters) {
            const activeFilterKeys = getActiveFilterKeys(filters);
            trackTeamsManagementEvent(
                "save team filters",
                `${teamName}/${activeFilterKeys.length}/${activeFilterKeys.join(",") || "none"}`,
            );
        },
        trackFiltersReset(teamName: string) {
            trackTeamsManagementEvent("reset team filters", teamName);
        },
        trackScoringSaved(teamName: string, ruleCount: number) {
            trackTeamsManagementEvent("save team lead scoring", `${teamName}/${ruleCount}`);
        },
        trackScoringReset(teamName: string) {
            trackTeamsManagementEvent("reset team lead scoring", teamName);
        },
        trackTerritoryFiltersToggled(isEnabled: boolean) {
            trackTeamsManagementEvent("toggle territory filters", isEnabled ? "on" : "off");
        },
        trackTerritoryTeamChanged(teamName: string) {
            trackTeamsManagementEvent("change territory team", teamName);
        },
        trackOpenCreateTeamModal(source: "header" | "empty_state") {
            trackTeamsManagementEvent("add team", source);
        },
        trackOpenAssignUserModal(source: AssignUserClickSource) {
            trackTeamsManagementEvent("assign user", source);
        },
        trackTabSwitch(tabName: TeamsManagementTabTrackingName) {
            trackTeamsManagementEvent("switch tab", tabName);
        },
        trackTeamDeleted(teamName: string) {
            trackTeamsManagementEvent("delete team", teamName);
        },
        trackOpenTeamDetail(teamName: string, source: "edit" | "name") {
            trackTeamsManagementEvent("open team detail", `${teamName}/${source}`);
        },
    };
};
